import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import { Resolvers } from '../../types/Resolvers';
import { getDataFromResolver } from '../../services/resolvers';
import { MarcaVehiculo } from '../../types/vehicle.types';
import { SmartCapitalizePipe } from '../../utils/pipes/smart-capitalize.pipe';
import { MarginWrapper } from '../../layouts/margin-wrapper';

@Component({
  selector: 'allas-baterias-vehiculos',
  imports: [
    MainLayout,
    FormsModule,
    CommonModule,
    RouterModule,
    SmartCapitalizePipe,
    MarginWrapper,
  ],
  template: `
    <allas-main-layout>
      <allas-margin-wrapper>
        <div class="px-4 py-8 fs-condensed">
          <!-- Titulo Principal oculto pero indexable -->
          <h1 class="absolute left-[-9999px]">
            Batería por Marca de Vehículo en Honduras
          </h1>
          <!-- Título simple -->
          <h2 class="text-2xl font-bold mb-8">
            Batería por Marca de Vehículo.
          </h2>

          @if (isLoading()) {
          <div class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <svg
                class="mx-auto w-8 h-8 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <span class="text-xl font-semibold text-gray-900 mb-2">
              Cargando marcas de vehículos...
            </span>
            <p class="text-gray-600">
              Estamos cargando la información más actualizada de vehículos.
            </p>
          </div>
          } @else {
          <!-- Lista agrupada alfabéticamente -->
          <div class="columns-1 md:columns-3 lg:columns-4 gap-8 space-y-8">
            @for (letter of getLetters(); track letter) {
            <div class="break-inside-avoid space-y-4">
              <h3 class="text-lg font-bold mb-4 pb-2 border-b border-allasRed">
                {{ letter }}
              </h3>
              <ul class="space-y-2">
                @for (brand of getBrandsByLetter(letter); track brand.slug) {
                <li>
                  <a
                    [routerLink]="['/bateria/para', brand.slug]"
                    class="text-gray-700 hover:text-allasRed text-sm block cursor-pointer"
                  >
                    Batería para {{ brand.name | smartCapitalize }}
                  </a>
                </li>
                }
              </ul>
            </div>
            }
          </div>
          }
        </div>
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
})
export class BateriasVehiculos implements OnInit {
  private route = inject(ActivatedRoute);
  private title = inject(Title);
  private meta = inject(Meta);
  private router = inject(Router);

  isLoading = signal(true);
  vehicleBrands: MarcaVehiculo[] = [];

  constructor() {}

  ngOnInit() {
    this.setMetaTags();
    this.loadVehicleBrands();
  }

  private setMetaTags() {
    const route = this.router.url;
    const pageTitle = 'Baterías por Marca de Vehículo | Allas Repuestos';
    const description =
      'Encuentra la batería perfecta para tu vehículo. Amplia selección de marcas con envío a domicilio en Honduras.';
    const keywords =
      'baterías, repuestos, vehículos, marcas, Honda, Toyota, Nissan, Ford, Chevrolet, envío a domicilio, Honduras, Allas Repuestos, instalación, garantía';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });
    this.meta.updateTag({ property: 'og:url', content: route });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }

  private async loadVehicleBrands(): Promise<void> {
    try {
      const data = getDataFromResolver<MarcaVehiculo[]>(
        Resolvers.ALL_VEHICLE_BRANDS,
        this.route
      );

      if (!data) {
        console.error('No se encontraron datos de marcas de vehículos');
        return;
      }

      this.vehicleBrands = data;
    } catch (error) {
      console.error('Error cargando marcas de vehículos:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  // Agrupar marcas por letra
  private marcasAgrupadas = computed(() => {
    const marcasPorLetra: {
      [key: string]: Array<{ name: string; slug: string }>;
    } = {};

    this.vehicleBrands.forEach((marca) => {
      const primeraLetra = marca.marca.charAt(0).toUpperCase();
      const slug = marca.marca
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');

      if (!marcasPorLetra[primeraLetra]) {
        marcasPorLetra[primeraLetra] = [];
      }

      marcasPorLetra[primeraLetra].push({
        name: marca.marca,
        slug: slug,
      });
    });

    return marcasPorLetra;
  });

  getLetters(): string[] {
    return Object.keys(this.marcasAgrupadas()).sort();
  }

  getBrandsByLetter(letter: string): Array<{ name: string; slug: string }> {
    return this.marcasAgrupadas()[letter] || [];
  }

  goBack(): void {
    window.history.back();
  }

  goHome(): void {
    // Implementar navegación a home
  }
}
